import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles } from 'lucide-react';

interface GalleryProps {
  images: readonly string[];
  interval?: number;
  transitionDuration?: number;
  effect?: 'fade' | 'fade-scale' | 'slide';
}

export default function Gallery({ 
  images, 
  interval = 6000, 
  transitionDuration = 1000,
  effect = 'fade-scale' 
}: GalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextImage = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(current => (current + 1) % images.length);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, transitionDuration);
  }, [images.length, transitionDuration]);

  useEffect(() => {
    const timer = setInterval(nextImage, interval);
    return () => clearInterval(timer);
  }, [interval, nextImage]);

  const getTransitionStyles = (index: number) => {
    const isActive = index === currentIndex;
    const baseStyles = 'absolute inset-0 transition-all ease-in-out';
    
    switch (effect) {
      case 'fade-scale':
        return `${baseStyles} ${
          isActive 
            ? 'opacity-100 scale-100' 
            : 'opacity-0 scale-95'
        }`;
      case 'slide':
        return `${baseStyles} ${
          isActive 
            ? 'translate-x-0 opacity-100' 
            : 'translate-x-full opacity-0'
        }`;
      default:
        return `${baseStyles} ${
          isActive 
            ? 'opacity-100' 
            : 'opacity-0'
        }`;
    }
  };

  return (
    <div className="relative aspect-square overflow-hidden">
      {images.map((src, index) => (
        <div
          key={src}
          className={getTransitionStyles(index)}
          style={{ 
            transitionDuration: `${transitionDuration}ms`,
            zIndex: index === currentIndex ? 1 : 0 
          }}
        >
          <img
            src={src}
            alt={`Showcase ${index + 1}`}
            className="w-full h-full object-cover transform transition-transform duration-1000 
              group-hover:scale-110"
            loading={index === 0 ? 'eager' : 'lazy'}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary-light/20 
            mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
        </div>
      ))}
      
      {/* Interactive elements */}
      <div className="absolute top-2 right-2">
        <Sparkles className="w-4 h-4 text-primary animate-pulse" />
      </div>
    </div>
  );
}